import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-tab-strip>` says about itself.
 *
 * The distinction this piece is most often on the wrong side of is with SegmentedFilter. They look
 * alike — a short row of small uppercase words with one lit — and they say different things:
 *
 *   TabStrip swaps the SUBJECT of a region. Details, Related, History on one record: different
 *   content occupies the same space. It is a real ARIA tablist, the active tab carries an accent
 *   underline, and it can OWN the panels below it.
 *
 *   SegmentedFilter narrows a SET. The same records, fewer of them. It is a group of pressed
 *   buttons on a list toolbar and it owns nothing beneath it.
 *
 * The panels are the half most likely to be missed. Each tab has a slot of its own, named after the
 * tab — the tab whose value is `details` takes its panel in `slot="panel-details"` — and whatever
 * arrives there is wired up as a `role="tabpanel"`: given an id if it has none, labelled by its tab,
 * pointed at by the tab's `aria-controls`, shown when its tab is active and `hidden` otherwise. The
 * second example uses it, because a tab row whose panels are switched by hand somewhere else is a tab
 * row that has given up half of what it does.
 *
 * The slot NAME carries the association because it is the only part of a stored placement that could
 * (CAOS-1572). A placement holds a component key, that component's declared inputs, and the name of
 * the slot the piece goes into; the older way of saying it — `slot="panel"` plus a `data-value` on
 * the panel — is an attribute set on the element, which no page assembled from metadata can write and
 * which no panel component could declare an input for without every possible panel declaring it too.
 * That older way still works, and is still what hand-written markup and the workspace's own record
 * page use.
 *
 * Panels are optional, and the piece is complete without them: the panels region collapses when
 * nothing is slotted, and a page that wants to draw the body itself listens for `change` instead.
 */
const docs: ComponentDocs = {
  aliases: ['tabs', 'tab bar', 'facets', 'section tabs', 'record tabs'],

  answers: [
    'split one record into Details, Related and History',
    'put tabs across a record page',
    'show one of several panels at a time',
    'break a long Setup page into sections without scrolling',
    'let somebody move between views of the same thing with the keyboard',
  ],

  // Facets of ONE record, or of one configuration object on a Setup page. Not a list-filtering
  // control — that is SegmentedFilter — so the list surfaces are not granted here.
  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'items',
      // No attribute: a list of tabs cannot be written in one. Plain strings are accepted as a
      // shorthand and expanded into { value, label } by the setter.
      type: 'json',
      description:
        'The tabs, as { value, label, disabled? } objects or as plain strings, in the order they are ' +
        'drawn. A disabled tab is drawn faded, cannot be chosen, and is skipped by the arrow keys.',
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'text',
      description:
        'Which tab is active, and therefore which panel shows. Reflected as an attribute, so it can be ' +
        'read back off the element. When the page sets none, the first ENABLED tab becomes the value as ' +
        'soon as the tabs are drawn.',
    },
    {
      // CAOS-1572 — declared, rather than left to `aria-label`, because a stored placement carries a
      // component's own inputs and nothing else. Distinct from the LABEL ON EACH TAB, which rides in
      // `items`: this one names the row, the same way a grid's `label` names the grid.
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The accessible name of the tab row — what the tabs are facets OF, in the words a screen reader ' +
        'reads out: Record facets, Order facets. Follows the attribute whenever it changes. Without one ' +
        'the row is announced as "Tabs", which distinguishes nothing on a page carrying two.',
    },
  ],

  events: [
    {
      name: 'change',
      when:
        'A tab becomes active and was not already — by pointer, or by an arrow key, which moves focus ' +
        'and activates in one gesture. Re-choosing the active tab raises nothing.',
      carries: '{ value } — the value of the tab now active.',
    },
  ],

  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [
    {
      // CAOS-1572 — one slot per tab, named after the tab's own value, so the association is the
      // slot name. That is the only part of a stored placement that can carry it.
      name: 'panel-<tab value>',
      takes:
        'The panel for the tab whose value this names — `panel-details` for the tab valued `details`. ' +
        'Wired up as a tabpanel: labelled by its tab, focusable, shown when its tab is active and ' +
        '`hidden` when it is not. Optional, and per tab: a tab with no slot filled simply has no ' +
        'panel, and a page may fill some and leave others to be drawn elsewhere. More than one ' +
        'element in one tab\’s slot is allowed, and all of them are that tab\’s panel.',
    },
    {
      name: 'panel',
      takes:
        'The older way of saying the same thing, still supported and still what hand-written markup ' +
        'usually says: one element per tab in a single shared slot, each carrying `data-value` ' +
        'matching its tab. A `data-value` is set on the element rather than declared, so a page ' +
        'assembled from stored metadata cannot write one — which is why the per-tab slots above ' +
        'exist. Elements whose `data-value` matches no tab are simply left alone.',
    },
  ],

  tokens: [
    { token: '--caos-color-accent', controls: 'The underline under the active tab.' },
    { token: '--caos-color-border', controls: 'The rule the tab row sits on.' },
    { token: '--caos-color-text', controls: 'The ink of the active tab and of a hovered one.' },
    { token: '--caos-color-text-muted', controls: 'The ink of the tabs that are not active.' },
    { token: '--caos-font-mono', controls: 'The typeface of the tab labels.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a keyboard-focused tab.' },
    { token: '--caos-space-4', controls: 'The gap between tabs, and the space above the panel body.' },
  ],

  parts: [
    { part: 'tablist', is: 'the row of tabs' },
    { part: 'tab', is: 'one tab' },
    { part: 'panels', is: 'the area the panels are slotted into' },
  ],

  methods: [],

  placing:
    'Across the full width of the region whose content it swaps — under a RecordHeader on a record ' +
    'page, or at the top of a configuration surface. It draws its own rule under the tab row, so ' +
    'nothing needs to add a divider. Past the width available the row scrolls sideways rather than ' +
    'wrapping, so a long set stays one line. If the panels are slotted into it, it owns the body ' +
    'beneath it too; if not, the surface listens for `change` and swaps the body itself.',

  whenToUse:
    'One subject with several faces, only one of which is worth looking at at a time. Reach for it when ' +
    'the alternative is a very long page a person has to scroll to find the part they came for.',

  insteadUse: [
    {
      name: 'SegmentedFilter',
      when:
        'The row NARROWS A SET rather than swapping the subject — All, Active, Mine on a list of ' +
        'records. That one is announced as pressed buttons rather than as tabs, draws as a filled pill ' +
        'rather than an underline, and belongs on the toolbar of the list it filters.',
    },
    {
      name: 'WorkspaceTabs',
      when:
        'You are moving between OBJECTS in an app rather than between facets of one record. That is a ' +
        'part of the workspace frame and is not placed on a page at all.',
    },
    {
      name: 'DetailSection',
      when:
        'Everything should be visible at once and only wants dividing up. Sections stack, so a person ' +
        'can read the whole record and print it; tabs hide what they are not showing.',
    },
    {
      name: 'SplitView',
      when: 'A person is moving between several RECORDS while keeping the list in sight, not between facets of one.',
    },
  ],

  accessibility:
    'A real WAI-ARIA tablist: `role="tablist"` around `role="tab"` buttons, `aria-selected` on the ' +
    'active one, and any slotted panel wired up as `role="tabpanel"` with `aria-labelledby` back to its ' +
    'tab and `aria-controls` forward to the panel — the author puts the panel in that tab\’s slot and ' +
    'gets all of that. Keyboard is a roving tabindex: one Tab stop for the whole row, then Arrow ' +
    'Left/Right/Up/Down, Home and End move and activate, skipping disabled tabs. Panels are made ' +
    'focusable so Tab from the row lands in the content. Left to the author: set `label` saying what ' +
    'the tabs are facets OF — the default name is "Tabs", which distinguishes nothing on a page with ' +
    'two rows. `label` is an input rather than an `aria-label` attribute so that a page assembled from ' +
    'stored metadata can set it; `aria-label` written straight onto the element is still read, and ' +
    '`label` wins where both are given. Because arrow keys activate as they move, avoid a tab whose ' +
    'content is expensive to produce.',

  examples: [
    {
      key: 'facets',
      label: 'Facets of a record',
      summary: 'The tab row on its own, with the surface drawing the body itself and listening for the change.',
      placement: {
        component: 'caos-tab-strip',
        inputs: {
          label: 'Record facets',
          value: 'details',
          items: [
            { value: 'details', label: 'Details' },
            { value: 'related', label: 'Related' },
            { value: 'history', label: 'History' },
          ],
        },
      },
    },
    {
      key: 'panels',
      label: 'Owning its panels',
      summary:
        'The same tabs with slotted panels that really switch — each body sits in its own tab\’s slot, ' +
        'and the strip shows one and hides the rest without the surrounding page doing anything.',
      placement: {
        component: 'caos-tab-strip',
        inputs: {
          label: 'Order facets',
          value: 'details',
          items: [
            { value: 'details', label: 'Details' },
            { value: 'related', label: 'Related' },
            { value: 'history', label: 'History' },
          ],
        },
        children: [
          {
            component: 'caos-detail-section',
            inputs: { slot: 'panel-details', heading: 'Order information' },
            children: [
              { component: 'caos-key-value-row', inputs: { label: 'Amount', value: '$12,400' } },
              { component: 'caos-key-value-row', inputs: { label: 'Opened', value: '14 Mar 2026' } },
            ],
          },
          {
            component: 'caos-detail-section',
            inputs: { slot: 'panel-related', heading: 'Related records' },
            children: [
              { component: 'caos-key-value-row', inputs: { label: 'Account', value: 'Meridian Supply' } },
              { component: 'caos-key-value-row', inputs: { label: 'Owner', value: 'Dana Whitfield' } },
            ],
          },
          {
            component: 'caos-empty-state',
            inputs: { slot: 'panel-history', title: 'No changes recorded' },
            children: ['Edits to this record will be listed here.'],
          },
        ],
      },
    },
    {
      key: 'disabled',
      label: 'A facet that is not available',
      summary:
        'One tab disabled — drawn faded, refused to the pointer, and stepped over by the arrow keys ' +
        'rather than focused and then rejected.',
      placement: {
        component: 'caos-tab-strip',
        inputs: {
          label: 'Order facets',
          value: 'details',
          items: [
            { value: 'details', label: 'Details' },
            { value: 'related', label: 'Related' },
            { value: 'approvals', label: 'Approvals', disabled: true },
          ],
        },
      },
    },
  ],
};

export default docs;
